import { CONDITION_OPERATORS } from "../../../../../../policy-types";
import { IConditionValidatorData } from '../condition-validator'

export class Edge {

    static LIMIT_SIDE = {
        LEFT: false,
        RIGHT: true
    }
    public value: number;
    public inclusive: boolean;

    static getLimitEdge(edge1: Edge, edge2: Edge, isMin: boolean) {
        const method = isMin ? Math.min : Math.max;
        let result;
        if (edge1.value === edge2.value) {
            result = new Edge(edge1.value, edge1.inclusive && edge2.inclusive)
        } else {
            let value = method(edge1.value, edge2.value);
            let inclusive = value === edge1.value
                ? edge1.inclusive : edge2.inclusive;
            result = new Edge(value, inclusive)
        }

        return result;
    }

    constructor(value: number, inclusive = false) {
        this.value = value;
        this.inclusive = inclusive;
    }

    /** used for tests */
    equals(edge: Edge) {
        return edge.value === this.value && edge.inclusive === this.inclusive;
    }
}


export class Range {
    private from: Edge;
    private to: Edge;

    /** returns array of ranges for condition */
    static FromCondition(condition: IConditionValidatorData) {
        /** create range by the operator */
        const value = condition.compare_value.value ?? condition.compare_value;
        switch (condition.operator) {

            case CONDITION_OPERATORS.OP_SMALLER:
                return [new Range(
                    new Edge(Number.NEGATIVE_INFINITY, false),
                    new Edge(value as number, false)
                )];

            case CONDITION_OPERATORS.OP_SMALLER_OR_EQUAL:
                return [new Range(
                    new Edge(Number.NEGATIVE_INFINITY, false),
                    new Edge(value as number, true)
                )];

            case CONDITION_OPERATORS.OP_EQUAL:
                return [new Range(
                    new Edge(value as number, true),
                    new Edge(value as number, true)
                )];

            case CONDITION_OPERATORS.OP_GREATER:
                return [new Range(
                    new Edge(value as number, false),
                    new Edge(Number.POSITIVE_INFINITY, true)
                )];

            case CONDITION_OPERATORS.OP_GREATER_OR_EQUAL:
                return [new Range(
                    new Edge(value as number, true),
                    new Edge(Number.POSITIVE_INFINITY, true)
                )];

            case CONDITION_OPERATORS.OP_NOT_EQUAL:
                return [new Range(
                    new Edge(Number.NEGATIVE_INFINITY, false),
                    new Edge(value as number, false)
                ), new Range(
                    new Edge(value as number, false),
                    new Edge(Number.POSITIVE_INFINITY, false)
                )];

            default:
                throw new Error('unknown rules condition operator ' + condition.operator);
        }
    }

    constructor(edgeFrom?: Edge, edgeTo?: Edge) {
        if (!edgeFrom || !edgeTo) {
            edgeFrom = new Edge(Number.NEGATIVE_INFINITY);
            edgeTo = new Edge(Number.POSITIVE_INFINITY);
        }

        this.from = edgeFrom;
        this.to = edgeTo;
    }

    intersect(range: Range) {
        return new Range(
            Edge.getLimitEdge(this.from, range.from, Edge.LIMIT_SIDE.LEFT),
            Edge.getLimitEdge(this.to, range.to, Edge.LIMIT_SIDE.RIGHT)
        );
    }

    get valid() {
        if (this.from.value === this.to.value) {
            return this.from.inclusive && this.to.inclusive;
        }
        return this.from.value < this.to.value;
    }

    /** used for tests */
    equals(range: Range) {
        return this.from.equals(range.from) && this.to.equals(range.to);
    }
}

/** all conditions must have valid values at this point */
function getIntersections(conditionList: IConditionValidatorData[] = []) {
    /** start from infinite range, with each applied condition adjust the allowed range */
    let resultRangeList = [new Range()];

    /** foreach condition create inclusive range */
    conditionList.forEach((condition) => {

        const conditionRangeList = Range.FromCondition(condition);

        const intersections: Range[] = [];
        conditionRangeList.forEach(condRange => {
            resultRangeList.forEach(resRange => {
                intersections.push(condRange.intersect(resRange));
            });
        });

        resultRangeList = intersections.filter(i => i.valid);
    });

    return resultRangeList;
}

export default getIntersections;